import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Procurement AI client helpers (advisory layer).
 *
 * Every call hits `/api/procurement/ai/*`, which returns `{ aiEnabled, ... }`.
 * When AI is off (no GROQ key / flag disabled) the endpoints return
 * `aiEnabled:false` + null payloads, so callers should hide their AI panel
 * via `useAiEnabled()` and fall back to manual entry. Nothing here mutates —
 * results are DRAFTS the user reviews + applies through the normal forms.
 */

export function useAiEnabled() {
  const { data } = useQuery<{ aiEnabled: boolean }>({
    queryKey: ['procurement', 'ai', 'status'],
    queryFn: () => api.get('/api/procurement/ai/status'),
    staleTime: 5 * 60_000,
  });
  return !!data?.aiEnabled;
}

export interface AiQuoteItem {
  vendorName: string;
  vendorEmail: string;
  vendorId: string;
  rate: number;
  currency: 'INR' | 'USD' | 'EUR' | 'AED';
  exchangeRate: number;
  quotedQty: number;
  yom: string;
  deliveryTime: string;
  paymentTerms: string;
  remarks: string;
  confidence: number;
  matched: boolean;
}

export function aiQuoteIngest(body: { indentId: string; mode: 'text' | 'pdf'; text?: string; fileBase64?: string }) {
  return api.post<{ aiEnabled: boolean; items: AiQuoteItem[]; note?: string }>('/api/procurement/ai/quote-ingest', body);
}

export interface AiDocExtract {
  docType: string; customLabel: string; invoiceNo: string; amount: number; currency: string;
  date: string; blContainerNos: string[]; vessel: string; eta: string; confidence: number;
}
export function aiDocExtract(body: { indentId?: string; fileBase64: string }) {
  return api.post<{ aiEnabled: boolean; suggestion: AiDocExtract | null; warnings?: string[] }>('/api/procurement/ai/doc-extract', body);
}

export interface AiIndentSpec {
  itemType: string; productCode: string; size: string; grade: string;
  machinery: string; accessories: string[]; quantity: number; confidence: number;
}
export function aiIndentSpec(text: string) {
  return api.post<{ aiEnabled: boolean; suggestion: AiIndentSpec | null }>('/api/procurement/ai/indent-spec', { text });
}

export interface AiAwardReco {
  ranking: Array<{ vendorName: string; itemId: string; score: number; rationale: string }>;
  splitAllocation: Array<{ itemId: string; awardedQty: number }>;
  summary: string;
}
export function aiAwardReco(indentId: string) {
  return api.get<{ aiEnabled: boolean; suggestion: AiAwardReco | null; bids?: any[]; note?: string }>(`/api/procurement/ai/award-reco/${encodeURIComponent(indentId)}`);
}

export function aiCopilot(question: string) {
  return api.post<{ aiEnabled: boolean; answer: string; citations: Array<{ indentId: string; reason: string }> }>('/api/procurement/ai/copilot', { question });
}

export interface AiDraftSnag { description: string; severity: 'minor' | 'major' | 'critical' }
export function aiDraftSnags(body: { photoDataUrls?: string[]; notes?: string }) {
  return api.post<{ aiEnabled: boolean; snags: AiDraftSnag[] }>('/api/procurement/ai/draft-snags', body);
}

export interface AiNegotiationDraft { suggestedRate: number; justification: string; targetPct: number }
export function aiNegotiateDraft(body: { indentId: string; itemId: string; vendorName: string; currentRate: number; currency: string }) {
  return api.post<{ aiEnabled: boolean; suggestion: AiNegotiationDraft | null; context?: { lowestRivalRate?: number; rounds: number } }>('/api/procurement/ai/negotiate-draft', body);
}

/** Deterministic live FX (INR per 1 unit of currency). Not AI — wraps the
 *  backend's cached rate so forms show the same number the award locks. */
export function procurementFx(currency: string) {
  return api.get<{ currency: string; rate: number }>(`/api/procurement/fx/${encodeURIComponent(currency)}`);
}

/** Read a File as a data URL (base64) for the vision endpoints. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
